import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { getReporteDownload } from '../api';
import type { ReporteListItem, DesgloseReglas } from '../api';
import { Loader, ReglasConciliacionModal } from '../components';
import { estadoLabel, formatMXN, pctColor } from '../utils/format';
import { useEmpresa, EMPRESA_TODAS } from '../empresa';

const { Title, Paragraph } = Typography;

type EstadoFiltro = 'TODOS' | 'al_dia' | 'en_proceso' | 'atrasada';

export function MisConciliaciones() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { rfc, reportes, loading, error } = useEmpresa();
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado]     = useState<EstadoFiltro>('TODOS');
  const [descargando, setDescargando] = useState<string | null>(null);
  const [modalReglas, setModalReglas] = useState<{
    visible: boolean;
    rfc: string;
    periodo?: string;
    porcentaje: number;
    desglose?: DesgloseReglas;
  }>({ visible: false, rfc: '', porcentaje: 0 });

  const filtrados = useMemo(() => {
    return reportes.filter(r => {
      if (rfc !== EMPRESA_TODAS && r.rfc !== rfc) return false;
      const pct = r.resumen.porcentaje_conciliado;
      if (estado === 'al_dia'     && pct < 80) return false;
      if (estado === 'en_proceso' && (pct < 60 || pct >= 80)) return false;
      if (estado === 'atrasada'   && pct >= 60) return false;
      if (busqueda && !r.rfc.toLowerCase().includes(busqueda.toLowerCase())) return false;
      return true;
    });
  }, [reportes, rfc, estado, busqueda]);

  async function descargarExcel(rfc: string) {
    setDescargando(rfc);
    try {
      const { url, filename } = await getReporteDownload(rfc);
      if (!url) throw new Error('Sin URL');
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `conciliacion_${rfc}.xlsx`;
      a.click();
    } catch {
      message.error('No se Pudo Descargar el Excel.');
    } finally {
      setDescargando(null);
    }
  }

  const columns: ColumnsType<ReporteListItem> = [
    {
      title: 'RFC',
      dataIndex: 'rfc',
      key: 'rfc',
      width: 150,
      render: rfc => <Tag style={{ fontFamily: 'monospace' }}>{rfc}</Tag>,
    },
    {
      title: 'Período',
      key: 'periodo',
      width: 200,
      render: (_, r) => {
        const parts = [r.periodo?.inicio, r.periodo?.fin].filter(Boolean);
        return parts.length ? parts.join(' — ') : '—';
      },
    },
    {
      title: 'Conciliación',
      key: 'pct',
      width: 180,
      render: (_, r) => {
        const pct = r.resumen.porcentaje_conciliado || 0;
        const periodoParts = [r.periodo?.inicio, r.periodo?.fin].filter(Boolean);
        const periodoStr = periodoParts.length ? periodoParts.join(' — ') : undefined;
        return (
          <div
            style={{ cursor: 'pointer' }}
            title="Ver reglas de conciliación"
            onClick={() =>
              setModalReglas({
                visible: true,
                rfc: r.rfc,
                periodo: periodoStr,
                porcentaje: pct,
                desglose: r.desglose_reglas,
              })
            }
          >
            <Progress
              percent={Math.min(pct, 100)}
              size="small"
              strokeColor={pctColor(pct)}
              format={() => `${Math.round(pct)}%`}
              style={{ marginBottom: 0 }}
            />
          </div>
        );
      },
    },
    {
      title: 'Conciliados',
      key: 'matched',
      width: 90,
      align: 'right',
      render: (_, r) => (
        <strong style={{ color: '#198754' }}>
          {(r.resumen.matched + (r.resumen.matched_ia || 0)).toLocaleString('es-MX')}
        </strong>
      ),
    },
    {
      title: 'Sin CFDI',
      key: 'sin_cfdi',
      width: 90,
      align: 'right',
      render: (_, r) => (
        <span style={{ color: r.resumen.sin_cfdi > 0 ? '#ffc107' : '#6c757d' }}>
          {r.resumen.sin_cfdi}
        </span>
      ),
    },
    {
      title: 'Sin Mov.',
      key: 'sin_mov',
      width: 90,
      align: 'right',
      render: (_, r) => (
        <span style={{ color: r.resumen.sin_movimiento_banco > 0 ? '#fd7e14' : '#6c757d' }}>
          {r.resumen.sin_movimiento_banco}
        </span>
      ),
    },
    {
      title: 'Total Banco',
      key: 'total_banco',
      width: 130,
      align: 'right',
      render: (_, r) => (
        <Space direction="vertical" size={0} style={{ width: '100%', alignItems: 'flex-end' }}>
          <strong>{formatMXN(r.resumen.monto_total_banco)}</strong>
          {r.resumen_por_moneda && Object.keys(r.resumen_por_moneda).length > 1 && (
            <Tag color="gold" style={{ fontSize: 10, margin: 0 }}>⚠ multi-moneda</Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Diferencia',
      key: 'diferencia',
      width: 130,
      align: 'right',
      render: (_, r) => (
        <strong style={{ color: r.resumen.diferencia > 0 ? '#dc3545' : '#198754' }}>
          {formatMXN(r.resumen.diferencia)}
        </strong>
      ),
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 110,
      render: (_, r) => {
        const e = estadoLabel(r.resumen.porcentaje_conciliado || 0);
        return (
          <Tag style={{ background: e.bg, color: e.color, border: 'none', fontWeight: 700 }}>
            {e.text}
          </Tag>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 180,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/reportes/${r.rfc}${r.id ? `?id=${r.id}` : ''}`)}>
            Ver
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<DownloadOutlined />}
            loading={descargando === r.rfc}
            onClick={() => descargarExcel(r.rfc)}
            style={{ background: '#198754', borderColor: '#198754' }}
          >
            Excel
          </Button>
        </Space>
      ),
    },
  ];

  if (loading) return <Loader tip="Cargando Conciliaciones..." />;

  return (
    <div>
      <Title level={3} style={{ marginTop: 0, marginBottom: 4 }}>Mis Conciliaciones</Title>
      <Paragraph type="secondary" style={{ marginBottom: 20 }}>
        Listado de Conciliaciones Fiscales Disponibles
      </Paragraph>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Buscar por RFC..."
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            style={{ width: 220 }}
            allowClear
          />
          <Select
            value={estado}
            onChange={setEstado}
            style={{ width: 200 }}
            options={[
              { value: 'TODOS',      label: 'Todos los Estados' },
              { value: 'al_dia',     label: 'Al Día (≥80%)' },
              { value: 'en_proceso', label: 'En Proceso (60–79%)' },
              { value: 'atrasada',   label: 'Atrasada (<60%)' },
            ]}
          />
          {(busqueda || estado !== 'TODOS') && (
          <Button type="link" onClick={() => { setBusqueda(''); setEstado('TODOS'); }}>
            Limpiar Filtros
          </Button>
          )}
          <Typography.Text type="secondary">
            {filtrados.length} Conciliación{filtrados.length !== 1 ? 'es' : ''}
          </Typography.Text>
        </Space>
      </Card>

      <Table<ReporteListItem>
        rowKey="rfc"
        columns={columns}
        dataSource={filtrados}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        size="middle"
        scroll={{ x: 1200 }}
      />

      <ReglasConciliacionModal
        visible={modalReglas.visible}
        onClose={() => setModalReglas(prev => ({ ...prev, visible: false }))}
        rfc={modalReglas.rfc}
        periodo={modalReglas.periodo}
        porcentaje={modalReglas.porcentaje}
        desglose={modalReglas.desglose}
      />
    </div>
  );
}
